import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { clp, fmtDate, OVERNIGHT, toISO } from "@/lib/format";
import { AvailabilityCalendar } from "@/components/booking/AvailabilityCalendar";
import { StepExperience, expAvailability } from "@/components/booking/StepExperience";
import { StepExtras } from "@/components/booking/StepExtras";
import { SummaryCard } from "@/components/booking/SummaryCard";
import { StepCustomer, customerErrors } from "@/components/booking/StepCustomer";
import { StepTransfer } from "@/components/booking/StepTransfer";
import { ThankYou } from "@/components/booking/ThankYou";

const EMPTY = { nombre: "", apellido: "", rut: "", telefono: "", email: "", direccion: "", comuna: "", ciudad: "", observaciones: "" };
const LABELS = { 1: "Fecha", 2: "Experiencia", 3: "Personaliza", 4: "Resumen", 5: "Datos", 6: "Transferencia" };
const TITLES = { 1: "Selecciona la fecha", 2: "Elige tu experiencia", 3: "Personaliza tu experiencia", 4: "Resumen de tu reserva", 5: "Tus datos", 6: "Transferencia bancaria" };

export default function BookingWizard({ preset, experiences, extras, settings }) {
  const [mode, setMode] = useState("dia");
  const [date, setDate] = useState(null);
  const [range, setRange] = useState({});
  const [exp, setExp] = useState(null);
  const [guests, setGuests] = useState(2);
  const [units, setUnits] = useState(1);
  const [sel, setSel] = useState({});
  const [step, setStep] = useState(1);
  const [avail, setAvail] = useState({});
  const [quote, setQuote] = useState(null);
  const [qLoading, setQLoading] = useState(false);
  const [customer, setCustomer] = useState(EMPTY);
  const [accept, setAccept] = useState(false);
  const [file, setFile] = useState(null);
  const [showErr, setShowErr] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const top = useRef(null);

  const checkIn = mode === "dia" ? date : range.from ? toISO(range.from) : null;
  const checkOut = mode === "dia" ? date : range.to ? toISO(range.to) : null;
  const expObj = experiences.find((e) => e.slug === exp);
  const isCabana = exp === "cabana";
  const habitacion = extras.find((x) => x.adds_guests > 0);

  useEffect(() => {
    if (!preset) return;
    setMode(preset.mode);
    if (preset.guests) setGuests(preset.guests);
    setExp(preset.experience || null);
    setStep(1);
    setResult(null);
  }, [preset]);

  useEffect(() => {
    if (expObj && OVERNIGHT.includes(expObj.slug) !== (mode === "noche")) setExp(null);
  }, [mode, expObj]);

  useEffect(() => {
    if (!isCabana) setSel({});
  }, [isCabana]);

  useEffect(() => {
    if (isCabana && habitacion && guests > (expObj?.base_guests || 5) && !sel[habitacion.id]) {
      setSel((s) => ({ ...s, [habitacion.id]: 1 }));
      toast.info("Agregamos la habitación adicional para tu grupo");
    }
  }, [isCabana, habitacion, guests, expObj, sel]);

  const payload = useMemo(() => exp && checkIn && checkOut && (mode === "dia" || checkOut > checkIn) ? {
    experience: exp, check_in: checkIn, check_out: checkOut, guests, units,
    extras: isCabana ? Object.entries(sel).map(([id, qty]) => ({ id, qty })) : [],
  } : null, [exp, checkIn, checkOut, guests, units, sel, isCabana, mode]);

  useEffect(() => {
    if (!payload) { setQuote(null); return; }
    setQLoading(true);
    const t = setTimeout(() => {
      api.post("/public/quote", payload).then((r) => setQuote(r.data)).catch(() => setQuote(null)).finally(() => setQLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [payload]);

  const go = (n) => { setShowErr(false); setStep(n); setTimeout(() => top.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 20); };
  const datesOk = mode === "dia" ? !!date : !!(checkIn && checkOut && checkOut > checkIn);
  const quoteOk = !!quote?.ok && !qLoading;
  const canNext = { 1: datesOk, 2: !!exp && quoteOk, 3: quoteOk, 4: quoteOk }[step];

  const next = () => {
    if (step === 1) return go(2);
    if (step === 2) return go(isCabana ? 3 : 4);
    if (step === 3 || step === 4) return go(step + 1);
    if (step === 5) {
      if (Object.keys(customerErrors(customer)).length || !accept) { setShowErr(true); toast.error("Revisa los datos marcados"); return; }
      return go(6);
    }
  };
  const back = () => go(step === 4 && !isCabana ? 2 : step - 1);

  const submit = async () => {
    if (!file) { setShowErr(true); toast.error("Adjunta el comprobante de transferencia"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...payload, customer, accept_terms: accept }));
      fd.append("receipt", file);
      const r = await api.post("/public/reservations", fd);
      setResult(r.data);
      setAvail({});
      go(7);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setResult(null); setDate(null); setRange({}); setExp(null); setSel({}); setCustomer(EMPTY); setAccept(false); setFile(null); go(1);
  };

  const steps = [1, 2, ...(isCabana ? [3] : []), 4, 5, 6];
  const expList = experiences.filter((e) => OVERNIGHT.includes(e.slug) === (mode === "noche"));
  const anyAvail = datesOk && expList.some((e) => expAvailability(e, checkIn, checkOut, avail).ok);

  return (
    <section id="reservar" ref={top} className="py-16 sm:py-24 bg-[#F3EDE2]/60 scroll-mt-16" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="eyebrow">Reserva en línea</p>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">Consulta disponibilidad y reserva</h2>
        {step === 7 && result ? (
          <div className="mt-10"><ThankYou result={result} phone={settings.contact.whatsapp} onReset={reset} /></div>
        ) : (
          <>
            <ol className="mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="booking-steps">
              {steps.map((n, i) => (
                <li key={n} data-testid={`booking-step-indicator-${n}`}
                  className={`shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${n === step ? "bg-[#5E6B52] text-white" : steps.indexOf(step) > i ? "bg-[#E9EFE6] text-[#4A5540]" : "bg-white text-muted-foreground"}`}>
                  <span className="tabular-nums">{i + 1}</span>{LABELS[n]}
                </li>
              ))}
            </ol>
            <div className="mt-8 grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2 rounded-3xl bg-[#FAF7F2] border border-border p-5 sm:p-8">
                <h3 className="font-serif text-2xl sm:text-3xl mb-6" data-testid="booking-step-title">{TITLES[step]}</h3>
                {step === 1 && (
                  <>
                    <div className="flex rounded-full bg-[#F3EDE2] p-1 w-full sm:w-fit mb-6">
                      {[["dia", "Visita por el día", Sun], ["noche", "Alojamiento (noches)", Moon]].map(([k, l, Icon]) => (
                        <button key={k} type="button" data-testid={`booking-mode-${k}`} onClick={() => { setMode(k); setDate(null); setRange({}); }}
                          className={`flex-1 flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${mode === k ? "bg-[#5E6B52] text-white" : "hover:bg-white"}`}>
                          <Icon className="h-4 w-4" />{l}
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mb-5">{mode === "dia" ? "Elige el día de tu visita." : "Elige la fecha de entrada y luego la de salida."}</p>
                    <AvailabilityCalendar mode={mode} date={date} range={range} onDate={setDate} onRange={setRange} avail={avail} setAvail={setAvail} />
                    {datesOk && (
                      <p className="mt-5 text-sm font-medium" data-testid="selected-dates">
                        {mode === "dia" ? fmtDate(date, true) : `${fmtDate(checkIn)} → ${fmtDate(checkOut)}`}
                        {!anyAvail && <span className="block text-[#C84B31]">No hay experiencias disponibles en estas fechas.</span>}
                      </p>
                    )}
                  </>
                )}
                {step === 2 && <StepExperience experiences={experiences} extras={isCabana ? extras.filter((x) => x.adds_guests > 0) : []} mode={mode} checkIn={checkIn} checkOut={checkOut}
                  avail={avail} exp={exp} setExp={setExp} guests={guests} setGuests={setGuests} units={units} setUnits={setUnits} />}
                {step === 2 && quote?.errors?.length > 0 && <div className="mt-4"><SummaryCard quote={{ errors: quote.errors }} /></div>}
                {step === 3 && <StepExtras extras={extras} selected={sel} setSelected={setSel} checkIn={checkIn} checkOut={checkOut}
                  avail={avail} guests={guests} baseGuests={expObj?.base_guests || 5} />}
                {step === 4 && <SummaryCard quote={quote} loading={qLoading} />}
                {step === 5 && <StepCustomer customer={customer} setCustomer={setCustomer} accept={accept} setAccept={setAccept} policies={settings.policies} showErrors={showErr} />}
                {step === 6 && <StepTransfer bank={settings.bank} deposit={quote?.deposit || 0} file={file} setFile={setFile} showErrors={showErr} />}

                <div className="mt-8 flex items-center justify-between gap-3">
                  {step > 1 ? (
                    <button type="button" onClick={back} data-testid="booking-back-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 font-semibold transition-colors hover:bg-[#F3EDE2]">
                      <ArrowLeft className="h-4 w-4" /> Atrás
                    </button>
                  ) : <span />}
                  {step < 6 ? (
                    <button type="button" onClick={next} disabled={step !== 5 && !canNext} data-testid="booking-next-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-[#5E6B52] text-white px-6 py-3 font-semibold transition-colors hover:bg-[#4A5540] disabled:opacity-40">
                      {step === 2 && !isCabana ? "Ver resumen" : "Continuar"} <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button type="button" onClick={submit} disabled={sending || !quoteOk} data-testid="submit-reservation-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-[#C86D51] text-white px-6 py-3.5 font-semibold transition-colors hover:bg-[#B25C42] disabled:opacity-50">
                      {sending && <Loader2 className="h-4 w-4 animate-spin" />} Enviar solicitud de reserva
                    </button>
                  )}
                </div>
              </div>
              <aside className="hidden lg:block sticky top-24">
                <SummaryCard quote={quote} loading={qLoading} compact={step < 3} />
              </aside>
            </div>
            {quote?.total !== undefined && step >= 2 && (
              <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#FAF7F2]/95 backdrop-blur-xl border-t border-border px-4 py-3 pr-24 flex items-center gap-4" data-testid="mobile-total-bar">
                <div>
                  <p className="text-xs text-muted-foreground">Total · Abono {clp(quote.deposit)}</p>
                  <p className="font-serif text-xl" data-testid="mobile-total">{clp(quote.total)}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
